/* eslint-disable */
import { useState, useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchNotification,
  markASread,
} from '../../../app/store/Notification/ActionCreator'
import {
  hasSeenToast,
  markToastSeen,
  isOlderThan24Hours,
} from '../../../helpers/utils'
import { isTPlus } from '../../../app/Auth/Auth'
import { INTIAL_STATE_TYPE } from '../../../app/store/NationalBrand/ActionType'

export const useNotifications = () => {
  const state = useSelector((state) => state || {})
  const { nbrVendorConfirm } = useSelector((state) => state.nationalBrand)
  const dispatch = useDispatch()
  const fetchedIds = useRef(new Set())
  const { lanId } = state?.auth || {}
  const notifications = state?.notificationReducer?.allNotifications?.data || []
  const [open, setOpen] = useState(false)
  const [newNotifs, setNewNotifs] = useState([])
  const [olderNotifs, setOlderNotifs] = useState([])
  const [page, setPage] = useState(0)
  const [toast, setToast] = useState(null)
  const [filterAnchor, setFilterAnchor] = useState(null)
  const [viewFilter, setViewFilter] = useState('')
  const [readStatusFilter, setReadStatusFilter] = useState('all') // 'all' or 'unread'
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isVisible, setIsVisible] = useState(
    document.visibilityState === 'visible'
  )
  const [progress, setProgress] = useState(100)

  const SNACKBAR_DURATION = 4000
  const PAGE_SIZE = 12
  const POLL_INTERVAL = 1000 * 60 * 5

  // Helper function to determine if user is national brand
  const getIsNationalBrand = () => {
    const isTPlusUser = Boolean(isTPlus)
    const hasVendorId = Boolean(nbrVendorConfirm?.vendor_id)
    const result = isTPlusUser || hasVendorId
    return result
  }

  const fetchLatest = useCallback(
    (filter = '') => {
      const payload = {
        start: 0,
        size: PAGE_SIZE,
        filter: filter,
      }

      if (filter && filter !== '') {
        const fromTime = new Date(Date.now() - 5 * 60 * 1000).toISOString()
        payload.from = fromTime
      }

      dispatch(
        fetchNotification({
          ...payload,
          isNationalBrand: getIsNationalBrand(),
        })
      )
    },
    [dispatch, lanId, nbrVendorConfirm]
  )

  const fetchOlder = useCallback(
    async (nextPage) => {
      try {
        setIsLoadingMore(true)
        if (nextPage === 0) {
          return
        }
        await dispatch(
          fetchNotification({
            start: nextPage * PAGE_SIZE,
            size: PAGE_SIZE,
            filter: viewFilter === '' ? '' : viewFilter,
            isNationalBrand: getIsNationalBrand(),
          })
        )
        setIsLoadingMore(false)
      } catch (e) {
        console.error('fetchOlder failed:', e)
      } finally {
        setIsLoadingMore(false)
      }
    },
    [viewFilter, notifications, dispatch, nbrVendorConfirm]
  )

  const markRead = async (n) => {
    try {
      await dispatch(
        markASread({
          notification_ids: [n.id],
          isNationalBrand: getIsNationalBrand(),
        })
      )

      // Update local state
      let updateNotification = {
        ...n,
        read: true,
      }
      if (!isOlderThan24Hours(n.created_time)) {
        setNewNotifs((prev) =>
          prev.map((notif) => (notif.id === n.id ? updateNotification : notif))
        )
      } else {
        setOlderNotifs((prev) =>
          prev.map((notif) => (notif.id === n.id ? updateNotification : notif))
        )
      }

      // Optionally refetch to ensure sync with server
      // fetchLatest(viewFilter)
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
    }
  }

  const markAllRead = async (notifications) => {
    try {
      const notificationIds = notifications.map((n) => n.id)
      await dispatch(
        markASread({
          notification_ids: notificationIds,
          isNationalBrand: getIsNationalBrand(),
        })
      )

      // Update local state
      notifications.forEach((n) => {
        const updateNotification = { ...n, read: true }
        if (!isOlderThan24Hours(n.created_time)) {
          setNewNotifs((prev) =>
            prev.map((notif) =>
              notif.id === n.id ? updateNotification : notif
            )
          )
        } else {
          setOlderNotifs((prev) =>
            prev.map((notif) =>
              notif.id === n.id ? updateNotification : notif
            )
          )
        }
      })

      // Optionally refetch to ensure sync with server
      // fetchLatest(viewFilter)
    } catch (error) {
      console.error('Failed to mark notifications as read:', error)
    }
  }

  const handleLoadMore = async () => {
    if (isLoadingMore) return
    setIsLoadingMore(true)
    try {
      await fetchOlder(page + 1)
      setPage(page + 1)
    } catch (e) {
      // handle error
    } finally {
      setIsLoadingMore(false)
    }
  }

  const handleFilterSelect = (filterKey) => {
    setViewFilter(filterKey)
    setOlderNotifs([])
    setNewNotifs([])
    fetchedIds.current.clear()
    setFilterAnchor(null)
    setPage(0)
    fetchLatest(filterKey === '' ? '' : filterKey)
  }

  const handleReadStatusSelect = (statusKey) => {
    setReadStatusFilter(statusKey)
    setFilterAnchor(null)
  }

  // Filter notifications based on read status
  const filterByReadStatus = (notifications) => {
    if (readStatusFilter === 'unread') {
      return notifications.filter((n) => !n.read)
    }
    return notifications
  }

  // Initial fetch
  useEffect(() => {
    dispatch(
      fetchNotification({
        start: 0,
        size: PAGE_SIZE,
        filter: viewFilter,
        isNationalBrand: getIsNationalBrand(),
      })
    )
  }, [dispatch, lanId, viewFilter, nbrVendorConfirm])

  // Toast progress
  useEffect(() => {
    if (toast) {
      setProgress(100)
      const interval = setInterval(() => {
        setProgress((old) => {
          if (old <= 0) {
            clearInterval(interval)
            return 0
          }
          return old - 100 / (SNACKBAR_DURATION / 100)
        })
      }, 100)
      return () => clearInterval(interval)
    }
  }, [toast])

  // Polling
  useEffect(() => {
    let pollInterval

    const startPolling = () => {
      fetchLatest()
      pollInterval = setInterval(() => {
        fetchLatest()
      }, POLL_INTERVAL)
    }

    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval)
      }
    }

    if (isVisible) {
      startPolling()
    } else {
      stopPolling()
    }

    return () => stopPolling()
  }, [fetchLatest, isVisible])

  // Process notifications
  useEffect(() => {
    if (notifications.length > 0) {
      let allToRender = notifications.filter(
        (n) => !fetchedIds.current.has(n.id)
      )
      if (viewFilter !== '') {
        allToRender = allToRender.filter((n) => n.event_type === viewFilter)
      }

      allToRender.forEach((n) => fetchedIds.current.add(n.id))

      const newNotifsArr = allToRender
        .filter((n) => !isOlderThan24Hours(n.created_time))
        .sort((a, b) => new Date(b.created_time) - new Date(a.created_time))
      const olderNotificationsArr = allToRender
        .filter((n) => isOlderThan24Hours(n.created_time))
        .sort((a, b) => new Date(b.created_time) - new Date(a.created_time))

      setNewNotifs((prev) => {
        const combined = [...prev, ...newNotifsArr]
        return combined.sort(
          (a, b) => new Date(b.created_time) - new Date(a.created_time)
        )
      })
      setOlderNotifs((prev) => {
        const combined = [...prev, ...olderNotificationsArr]
        return combined.sort(
          (a, b) => new Date(b.created_time) - new Date(a.created_time)
        )
      })

      // Toast trigger logic
      const toastCandidates = notifications
        .filter((n) => !isOlderThan24Hours(n.created_time))
        .filter((n) => viewFilter === '' || n.event_type === viewFilter)

      const firstUnseenToast = toastCandidates.find((n) => !hasSeenToast(n.id))

      if (isVisible && firstUnseenToast) {
        setToast(firstUnseenToast)
        markToastSeen(firstUnseenToast.id)
      }
    }
  }, [notifications, viewFilter, isVisible])

  // Visibility change
  useEffect(() => {
    const handler = () => setIsVisible(document.visibilityState === 'visible')
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [])

  return {
    // State
    open,
    setOpen,
    newNotifs: filterByReadStatus(newNotifs),
    olderNotifs: filterByReadStatus(olderNotifs),
    toast,
    setToast,
    filterAnchor,
    setFilterAnchor,
    viewFilter,
    readStatusFilter,
    isLoadingMore,
    progress,
    // Actions
    markRead,
    markAllRead,
    handleLoadMore,
    handleFilterSelect,
    handleReadStatusSelect,
    // Computed
    unreadCount: newNotifs.filter((n) => !n.read).length,
    hasNotifications: newNotifs.length > 0 || olderNotifs.length > 0,
  }
}
